import { useCallback, useMemo, useRef, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { GetWorkspaceUsersDocument, useAppData } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useMemberEntityResolver } from "#app/lib/data/member-directory-provider";
import {
  filterEntityItems,
  type EntityBadgeConfig,
  type EntityItemConfig,
  type EntityPoolConfig,
} from "@salestrak/ui";
import { resolveEntityLabel, type EntityData } from "#app/lib/entity-visuals";
import { isFormerMember } from "#app/lib/role-utils";
import { useEntityDetailOptional } from "#app/components/entity";
import { poolItemVisual } from "../utils";
import { useEntitySearchPool } from "./use-entity-search-pool";
import type { Omittable } from "@salestrak/ui";
import { DEFAULT_PAGE_SIZE } from "#app/lib/constants/page-size";

const PAGE_SIZE = DEFAULT_PAGE_SIZE;
const ALL_STATUSES = ["active", "invited"] as const;
const ACTIVE_ONLY_STATUSES = ["active"] as const;

const POOL_ID = "users";

// THE user → selector-item mapping (visual render, invited/suspended
// treatment, label fallback chain). Shared by every selector/filter listing users.
export function userToPoolItem(node: Omittable<{
  userId: string;
  name?: string | null;
  email?: string | null;
  profileImage?: string | null;
  memberStatus?: string | null;
}>): EntityItemConfig {
  const email = node.email ?? "";
  const entity: EntityData = {
    kind: "user",
    id: node.userId,
    user: {
      name: node.name,
      email,
      profileImage: node.profileImage,
      memberStatus: node.memberStatus,
    },
  };
  const invited = node.memberStatus === "invited";
  return {
    id: node.userId,
    title: node.name?.trim() || resolveEntityLabel(entity) || node.userId,
    description: invited ? "Invited" : undefined,
    invited,
    avatar: {
      src: node.profileImage ?? undefined,
      render: () => poolItemVisual(entity),
    },
    keywords: email ? [email] : [],
    // memberStatus is stashed here so optimistic-response builders can
    // round-trip the real value back into the Apollo cache write (the
    // *EntityRef fragments select User.memberStatus, and Apollo warns
    // when the optimistic value is missing a selected field).
    data: { email, entityType: "user", memberStatus: node.memberStatus ?? null },
  };
}

/**
 * Members who share a display name ("Brent Walter" twice) read the same in a
 * list, so each of them gets their email as the row's subtitle — beside
 * "Invited" when that is shown. Rows with a unique name are returned as-is.
 */
export function disambiguateSameNames(
  items: EntityItemConfig[],
): EntityItemConfig[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const key = item.title.trim().toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return items.map((item) => {
    const email = item.data?.email;
    if (typeof email !== "string" || !email) return item;
    if ((counts.get(item.title.trim().toLowerCase()) ?? 0) < 2) return item;
    return {
      ...item,
      description: item.description ? `${item.description} · ${email}` : email,
    };
  });
}

export type UseUserPoolOptions = Omittable<{
  /** User IDs to exclude from the pool (e.g., already-assigned members) */
  exclude?: string[];
  /** When true, only "active" members are returned. Defaults to including "invited" members so assignment selectors can target pending users. Filter selectors should opt in. */
  activeOnly?: boolean;
  /** The member statuses to list, in place of `activeOnly`'s choice — a
   *  filter lists `FILTER_MEMBER_STATUSES`. Empty falls back to that choice. */
  statuses?: readonly string[];
  /** The pool carries the hover "Profile" row action (open the member's
   *  profile) by DEFAULT — any selector popover listing members offers it.
   *  Pass false only where the row grammar has no room for it: the mention
   *  typeahead and menu submenus, whose trailing gutter is already spoken
   *  for. A list that passes its own `onItemAction` overrides the handler
   *  without needing this. */
  profileAction?: boolean;
  /** The viewer's own row LEADS by default — the one member every
   *  assignment reaches for — so it is the first row under "Members" and,
   *  once chosen, the first of the selected run (the selector orders `lead`
   *  items first inside their group), never an unchosen "me" above the
   *  chosen rows and never a band of its own. It comes from the boot-time
   *  profile, so it stands before any page loads and matches the search like
   *  any row. Pass false for a list where the viewer is not a candidate. */
  pinViewer?: boolean;
}>;

/**
 * Fetches workspace users and returns an EntityPoolConfig for use with EntitySelector.
 * Loads the first page on mount; additional pages load on demand via
 * `onLoadRange` as the user scrolls. Supports server-side search via
 * `onSearchChange` to find members not yet loaded.
 */
export function useUserPool(options?: UseUserPoolOptions) {
  const { exclude, activeOnly, statuses, profileAction = true, pinViewer = true } = options ?? {};
  const statusesKey = (
    statuses?.length ? statuses : activeOnly ? ACTIVE_ONLY_STATUSES : ALL_STATUSES
  ).join(",");
  const client = useApolloClient();
  const profileBadge = useProfileOpenBadge();
  const itemBadge = profileAction ? profileBadge : undefined;
  const { currentUser } = useAppData();

  // "Me", from the boot-time profile: present before any page loads and
  // whatever page the viewer's name would fall on. No "You" marker — the
  // row reads like every other member's; its place is the signal.
  const viewerItem = useMemo<EntityItemConfig | null>(() => {
    if (!pinViewer || !currentUser || exclude?.includes(currentUser.userId)) return null;
    return {
      ...userToPoolItem({
        userId: currentUser.userId,
        name: currentUser.name,
        email: currentUser.email,
        profileImage: currentUser.profileImage,
        memberStatus: "active",
      }),
      lead: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `exclude` by contents, below
  }, [pinViewer, currentUser, exclude?.join(",")]);
  // The paged list drops the viewer (the lead row stands in for that page's
  // copy), so the pool never draws the viewer twice.
  const excludeWithViewer = useMemo(
    () => (viewerItem ? [...(exclude ?? []), viewerItem.id] : exclude),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `exclude` by contents
    [exclude?.join(","), viewerItem],
  );
  // The pool is server-filtered, so the lead row answers the query itself.
  const [query, setQuery] = useState("");

  const memberStatusFilter = useMemo(
    () => ({
      where: {
        memberStatus: { in: statusesKey.split(",") },
      },
    }),
    [statusesKey],
  );

  const initialQueryVars = useMemo(() => ({ limit: PAGE_SIZE, skip: 0, ...memberStatusFilter }), [memberStatusFilter]);

  const { data, loading } = useQuery(GetWorkspaceUsersDocument, {
    variables: initialQueryVars,
  });

  const total: number = data?.users?.total ?? 0;

  const initialItems = useMemo(() => {
    if (!data?.users?.edges) return [];
    return data.users.edges.filter((edge) => edge?.node).map((edge) => userToPoolItem(edge.node));
  }, [data]);

  const extraMapRef = useRef(new Map<number, EntityItemConfig>());
  const [extraVersion, setExtraVersion] = useState(0);
  const fetchingRef = useRef<Set<number>>(new Set());
  const attemptedPagesRef = useRef<Set<number>>(new Set());
  const epochRef = useRef(0);

  // Reset lazily-loaded pages only when the FILTER changes — NOT when Apollo
  // re-emits `data`. `User` is a normalized, app-wide-shared cache entity, so
  // the base query's `data` reference churns constantly as unrelated queries
  // write Users (hover previews, workspace data, issue lists, …). Keying the
  // reset on that identity wiped every loaded page on each churn, so scrolling
  // showed permanent skeletons: pages were refetched but discarded before they
  // could render. memberStatusFilter is a stable memo that only changes when
  // the filter (activeOnly) actually changes — the one time stale absolute-index
  // pages truly must be dropped. Ref mutations only — no setState — to avoid
  // loops with cache re-emission. baseItems recomputes via initialItems dep.
  const filterEpoch = useRef(memberStatusFilter);
  if (filterEpoch.current !== memberStatusFilter) {
    filterEpoch.current = memberStatusFilter;
    epochRef.current += 1;
    extraMapRef.current = new Map();
    fetchingRef.current.clear();
    attemptedPagesRef.current = new Set();
  }
  if (data?.users?.edges) {
    attemptedPagesRef.current.add(0);
  }

  const loadRange = useCallback(
    (startIndex: number, endIndex: number) => {
      const lastNeeded = Math.min(total - 1, endIndex);
      const startPage = Math.floor(startIndex / PAGE_SIZE);
      const lastPage = Math.ceil(total / PAGE_SIZE) - 1;
      const endPage = Math.min(lastPage, Math.floor(lastNeeded / PAGE_SIZE) + 1);

      for (let page = startPage; page <= endPage; page++) {
        if (page === 0) continue;
        const skip = page * PAGE_SIZE;
        if (attemptedPagesRef.current.has(page) || fetchingRef.current.has(skip)) continue;

        const fetchEpoch = epochRef.current;
        fetchingRef.current.add(skip);
        client
          .query({
            query: GetWorkspaceUsersDocument,
            variables: { skip, limit: PAGE_SIZE, ...memberStatusFilter },
            fetchPolicy: "no-cache",
          })
          .then(({ data: result }) => {
            if (epochRef.current !== fetchEpoch) return;
            attemptedPagesRef.current.add(page);
            const edges = result?.users?.edges;
            if (edges) {
              const currentMap = extraMapRef.current;
              edges.forEach((edge, i) => {
                if (edge?.node) {
                  currentMap.set(skip + i, userToPoolItem(edge.node));
                }
              });
            }
            setExtraVersion((v) => v + 1);
          })
          .catch((err) => console.warn("[useUserPool] Failed to load page:", err))
          .finally(() => {
            fetchingRef.current.delete(skip);
          });
      }
    },
    [total, client, memberStatusFilter],
  );

  const baseItems = useMemo<EntityItemConfig[]>(() => {
    const seen = new Set<string>();
    const all: EntityItemConfig[] = [];
    for (const item of initialItems) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        all.push(item);
      }
    }
    extraMapRef.current.forEach((item) => {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        all.push(item);
      }
    });
    return all;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- extraVersion stands for the ref'd extra items
  }, [initialItems, extraVersion]);

  const runSearch = useCallback(
    async (trimmed: string): Promise<EntityItemConfig[]> => {
      const { data: result } = await client.query({
        query: GetWorkspaceUsersDocument,
        variables: {
          limit: PAGE_SIZE,
          skip: 0,
          where: {
            ...memberStatusFilter.where,
            search: trimmed,
          },
        },
        fetchPolicy: "no-cache",
      });
      if (!result?.users?.edges) return [];
      return result.users.edges.filter((edge) => edge?.node).map((edge) => userToPoolItem(edge.node));
    },
    [client, memberStatusFilter],
  );

  const { items: pagedItems, onSearchChange: onPagedSearchChange } = useEntitySearchPool({
    baseItems,
    runSearch,
    exclude: excludeWithViewer,
    // Reset cached server-search results on filter change, not on every `data`
    // re-emission — same churn problem as the paged extras above.
    epoch: memberStatusFilter,
  });
  const onSearchChange = useCallback(
    (next: string) => {
      setQuery(next.trim());
      onPagedSearchChange(next);
    },
    [onPagedSearchChange],
  );
  // The lead row heads the list inside the Members group (the selector
  // orders `lead` items first, so its place here only has to exist).
  const items = useMemo<EntityItemConfig[]>(() => {
    if (!viewerItem) return disambiguateSameNames(pagedItems);
    const shown = query ? filterEntityItems([viewerItem], query) : [viewerItem];
    return disambiguateSameNames(shown.length ? [...shown, ...pagedItems] : pagedItems);
  }, [pagedItems, query, viewerItem]);

  const lastPageNeeded = total > 0 ? Math.ceil(total / PAGE_SIZE) - 1 : -1;
  let allPagesAttempted = total > 0;
  for (let page = 0; page <= lastPageNeeded; page++) {
    if (!attemptedPagesRef.current.has(page)) {
      allPagesAttempted = false;
      break;
    }
  }

  // The caller's exclusions only: the viewer left the paged list but stands
  // in the pool as the lead row, so the virtual total still counts them.
  const excludeSize = exclude?.length ? new Set(exclude).size : 0;
  const adjustedTotal = allPagesAttempted
    ? items.length
    : excludeSize > 0
      ? Math.max(total - excludeSize, items.length)
      : total;

  // Stable, so a directory batch landing does not re-derive every consumer
  // of the pool; a memo that must re-derive then subscribes to
  // `useMemberItemResolver` itself.
  const resolveMemberItem = useMemberItemResolver();
  const resolveRef = useRef(resolveMemberItem);
  resolveRef.current = resolveMemberItem;
  const resolveItem = useCallback((id: string) => resolveRef.current(id), []);
  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: POOL_ID,
      label: "Members",
      items,
      itemBadge,
      total: adjustedTotal,
      onLoadRange: loadRange,
      onSearchChange,
      // Server-filtered already; client-filtering would hide server matches not in the initial pages.
      serverFiltered: true,
      resolveItem,
    }),
    [items, itemBadge, adjustedTotal, loadRange, onSearchChange, resolveItem],
  );

  return { pool, loading };
}

export { POOL_ID as USER_POOL_ID };

/**
 * A member's selector row by id, for any member — the bootstrap roster first,
 * else the member directory's batched fetch. Undefined until it lands, then
 * the resolver's identity changes so a memo over it re-derives.
 */
export function useMemberItemResolver() {
  const resolveMember = useMemberEntityResolver();
  return useCallback(
    (id: string): EntityItemConfig | undefined => {
      const entity = resolveMember(id);
      return entity?.kind === "user"
        ? userToPoolItem({ userId: id, ...entity.user })
        : undefined;
    },
    [resolveMember],
  );
}

/**
 * EVERY workspace member as a selector item, from the boot-time roster
 * (`useWorkspaceData`) — for surfaces that enumerate the whole membership at
 * once: the group-by-assignee bands and board columns. `useUserPool` is
 * WINDOWED — its items hold only the pages a selector has scrolled in, so a
 * list of groups built from it stops mid-alphabet at the first page boundary.
 * Suspended members are omitted; invited members can hold assignments, so
 * they stay, marked the way the pool marks them.
 */
export function useAllMemberItems(): EntityItemConfig[] {
  const { users } = useWorkspaceData();
  return useMemo(
    () =>
      disambiguateSameNames(
        users.filter((user) => !isFormerMember(user)).map(userToPoolItem),
      ),
    [users],
  );
}

/**
 * THE "Profile" row-action badge for pools listing members — the hover
 * button on picker rows that opens the member's profile. `useUserPool`
 * bakes it in; a hand-built member pool (a filter chip, a workspace-data
 * pool) spreads it as its `itemBadge`. Undefined without an entity-detail
 * provider, so the button never renders where it has nowhere to go.
 *
 * closeOnAction: the profile island is another surface — a picker left
 * open behind it reads as an unanswered question.
 */
export function useProfileOpenBadge(): EntityBadgeConfig | undefined {
  const detail = useEntityDetailOptional();
  const openProfile = useCallback(
    (entity: EntityItemConfig) => {
      detail?.openEntity("user", entity.id, { via: "user_picker" });
    },
    [detail],
  );
  // A Guest gets no "Profile" row action: profiles are withheld from them
  // (`viewerCanOpenProfiles`), so the badge would open nothing.
  // Nor inside a contained surface (an unsaved draft), which the profile would close.
  const available = detail?.viewerCanOpenProfiles === true && !detail.contained;
  return useMemo(
    () =>
      available
        ? {
            closeOnAction: true,
            label: "Profile",
            tooltip: "Open Profile",
            onAction: openProfile,
          }
        : undefined,
    [available, openProfile],
  );
}
